import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { MapPin, Phone, Instagram, Send } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, language, isRTL } = useLanguage();
  const [services, setServices] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service_type: '',
    course_or_service: '',
    preferred_date: '',
    message: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, coursesRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/courses`),
        ]);
        setServices(servicesRes.data);
        setCourses(coursesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/bookings`, formData);
      toast.success(t.contact.form.success);
      setFormData({
        name: '',
        phone: '',
        email: '',
        service_type: '',
        course_or_service: '',
        preferred_date: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'service_type') {
      setFormData((prev) => ({ ...prev, course_or_service: '' }));
    }
  };

  return (
    <div className="bg-[#FDFCF8] min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className={`${isRTL ? 'text-right' : 'text-left'} max-w-3xl`}>
            <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              {t.contact.subtitle}
            </p>
            <h1
              data-testid="contact-title"
              className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-4xl md:text-6xl font-medium text-[#1A1A1A] mb-6`}
            >
              {t.contact.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="pb-20 md:pb-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Form */}
            <div className="order-2 lg:order-1">
              <form
                onSubmit={handleSubmit}
                data-testid="booking-form"
                className={`space-y-6 ${isRTL ? 'text-right' : 'text-left'}`}
              >
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className={`${isRTL ? 'font-tajawal' : 'font-manrope'}`}>
                    {t.contact.form.name} *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="input-name"
                    className="bg-white border-[#E6E2D6] focus:border-[#C5A059] focus:ring-[#C5A059]/20 rounded-lg py-6"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className={`${isRTL ? 'font-tajawal' : 'font-manrope'}`}>
                    {t.contact.form.phone} *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="input-phone"
                    className="bg-white border-[#E6E2D6] focus:border-[#C5A059] focus:ring-[#C5A059]/20 rounded-lg py-6"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className={`${isRTL ? 'font-tajawal' : 'font-manrope'}`}>
                    {t.contact.form.email}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="input-email"
                    className="bg-white border-[#E6E2D6] focus:border-[#C5A059] focus:ring-[#C5A059]/20 rounded-lg py-6"
                  />
                </div>

                {/* Service Type */}
                <div className="space-y-2">
                  <Label className={`${isRTL ? 'font-tajawal' : 'font-manrope'}`}>
                    {t.contact.form.serviceType} *
                  </Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(value) => handleSelectChange('service_type', value)}
                  >
                    <SelectTrigger
                      data-testid="select-service-type"
                      className="bg-white border-[#E6E2D6] focus:border-[#C5A059] focus:ring-[#C5A059]/20 rounded-lg py-6"
                    >
                      <SelectValue placeholder={t.contact.form.selectOption} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="course">{t.contact.form.course}</SelectItem>
                      <SelectItem value="service">{t.contact.form.service}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Course or Service Selection */}
                {formData.service_type && (
                  <div className="space-y-2">
                    <Label className={`${isRTL ? 'font-tajawal' : 'font-manrope'}`}>
                      {formData.service_type === 'course' ? t.contact.form.course : t.contact.form.service} *
                    </Label>
                    <Select
                      value={formData.course_or_service}
                      onValueChange={(value) => handleSelectChange('course_or_service', value)}
                    >
                      <SelectTrigger
                        data-testid="select-course-service"
                        className="bg-white border-[#E6E2D6] focus:border-[#C5A059] focus:ring-[#C5A059]/20 rounded-lg py-6"
                      >
                        <SelectValue placeholder={t.contact.form.selectOption} />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.service_type === 'course'
                          ? courses.map((course) => (
                              <SelectItem key={course.id} value={language === 'en' ? course.title_en : course.title_ar}>
                                {language === 'en' ? course.title_en : course.title_ar}
                              </SelectItem>
                            ))
                          : services.map((service) => (
                              <SelectItem key={service.id} value={language === 'en' ? service.name_en : service.name_ar}>
                                {language === 'en' ? service.name_en : service.name_ar}
                              </SelectItem>
                            ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Preferred Date */}
                <div className="space-y-2">
                  <Label htmlFor="preferred_date" className={`${isRTL ? 'font-tajawal' : 'font-manrope'}`}>
                    {t.contact.form.preferredDate} *
                  </Label>
                  <Input
                    id="preferred_date"
                    name="preferred_date"
                    type="date"
                    value={formData.preferred_date}
                    onChange={handleChange}
                    required
                    data-testid="input-date"
                    className="bg-white border-[#E6E2D6] focus:border-[#C5A059] focus:ring-[#C5A059]/20 rounded-lg py-6"
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className={`${isRTL ? 'font-tajawal' : 'font-manrope'}`}>
                    {t.contact.form.message}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    data-testid="input-message"
                    className="bg-white border-[#E6E2D6] focus:border-[#C5A059] focus:ring-[#C5A059]/20 rounded-lg"
                  />
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-booking"
                  className="w-full rounded-full bg-[#C5A059] text-white hover:bg-[#B08A3E] py-6 text-lg font-medium shadow-[0_4px_14px_0_rgba(197,160,89,0.39)] hover:shadow-[0_6px_20px_rgba(197,160,89,0.23)] hover:-translate-y-1 transition-all duration-300"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      {t.contact.form.submit}
                      <Send className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className={`order-1 lg:order-2 ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="sticky top-32 space-y-8">
                {/* WhatsApp */}
                <a
                  href="https://wa.me/966599385282"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="group flex items-center gap-4 p-6 bg-white rounded-2xl border border-[#E6E2D6]/50 hover:border-[#C5A059]/30 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-full bg-[#C5A059]/10 flex items-center justify-center group-hover:bg-[#C5A059] transition-all duration-300">
                    <Phone className="w-6 h-6 text-[#C5A059] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-sm text-[#666660]`}>
                      {t.contact.whatsapp}
                    </p>
                    <p className="font-playfair text-lg text-[#1A1A1A]">+966 599 385 282</p>
                  </div>
                </a>

                {/* Instagram */}
                <a
                  href="https://instagram.com/ninaceban_riyadh"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram"
                  className="group flex items-center gap-4 p-6 bg-white rounded-2xl border border-[#E6E2D6]/50 hover:border-[#C5A059]/30 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-full bg-[#C5A059]/10 flex items-center justify-center group-hover:bg-[#C5A059] transition-all duration-300">
                    <Instagram className="w-6 h-6 text-[#C5A059] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-sm text-[#666660]`}>
                      {t.contact.instagram}
                    </p>
                    <p className="font-playfair text-lg text-[#1A1A1A]">@ninaceban_riyadh</p>
                  </div>
                </a>

                {/* Location */}
                <div className="flex items-center gap-4 p-6 bg-white rounded-2xl border border-[#E6E2D6]/50">
                  <div className="w-14 h-14 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-sm text-[#666660]`}>
                      {t.contact.location}
                    </p>
                    <p className="font-playfair text-lg text-[#1A1A1A]">{t.contact.riyadh}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
